/**
 * @author Matěj Grabovský <matej.grabovsky@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';

import ErrorName from '../error/error-name';
import SetupErrorType from './setup-error-type';

// TODO Aktuální Babel 6.26 nepodporuje `class SetupError extends Error`.
// - Proto je zatím jako hotfix použito ES5 zpracování.
// - Do budoucna buď aktualizovat Babel,
//   nebo vyčlenit do nějakého univerzálního řešení pro vlastní error objekty.
// TODO Mohlo by být zpracováno lépe z pohledu OOP, než jen jako číselník se switch.
// - Vyžadovalo by nasazení jednoduchého způsobu jak rozšiřovat Error.
// - Spousta nativních errorů také používá interní kódy např. MediaError, DOMException.
// - Kompromis mezi opakováním kódu, OOP good-practices a složitostí.
// - Po omezení funkcionality navíc nakonec není příliš využíváno.
//   Zatím ponecháno minimálně jako základ pro budoucí vlastní error.
/**
 * Chyba vyskytující se při registraci zařízení na serveru.
 * @class
 * @classdesc Vytvoří instanci.
 * @implements {Error}
 * @name SetupError
 * @param {SetupErrorType} type
 */
function SetupError(type) {
  const message = SetupError.getMessage(type);
  // Vytvoří se nativní error, který má schopnost zachytit zásobník volání.
  const error = new Error(message);
  // Nastaví se název nativnímu error objektu, aby se zobrazil v jeho zprávě.
  error.name = ErrorName.SetupError;
  // Definuje se vlastnost se zprávou jako u nativního objektu.
  if (!isNil(message)) {
    Object.defineProperty(this, 'message', {
      configurable: true,
      value: error.message,
      writable: true
    });
  }
  // Definuje se vlastnost se zásobníkem jako u nativního objektu.
  // Interně se při vyhodnocení zásobníku použije připravená zpráva.
  Object.defineProperty(this, 'stack', {
    configurable: true,
    value: error.stack,
    writable: true
  });
}

/**
 * Zjistí zprávu dle typu chyby.
 * @function
 * @name SetupError.getMessage
 * @param {SetupErrorType} type
 * @returns {?string}
 */
SetupError.getMessage = function getMessage(type) {
  switch (type) {
    case SetupErrorType.AccessCodeMissing:
      return 'Missing device access code.';
    case SetupErrorType.DeviceExists:
      return 'Device ID already exists.';
    case SetupErrorType.DeviceLimitExceeded:
      return 'License limit for number of devices was exceeded.';
    case SetupErrorType.DeviceRegistered:
      return 'Device is already registered.';
    case SetupErrorType.OrganizationInvalid:
      return 'Unable to add the device to organization.';
    case SetupErrorType.OrganizationMissing:
      return 'Missing device organization.';
    case SetupErrorType.RequestInvalid:
      return 'Invalid request format.';
    case SetupErrorType.SaveError:
      return 'Unable to save the device.';
    case SetupErrorType.UserInvalid:
      return 'Invalid username or password.';
    default:
    // Ve výchozím stavu není definováno.
  }
};

SetupError.prototype = Object.create(Error.prototype, {
  constructor: {
    configurable: true,
    value: SetupError,
    writable: true
  },
  message: {
    configurable: true,
    value: '',
    writable: true
  },
  name: {
    configurable: true,
    value: ErrorName.SetupError,
    writable: true
  },
  toString: {
    configurable: true,
    value: function toString() {
      return this.error.toString();
    },
    writable: true
  }
});

export default SetupError;
